import moment from 'moment';
import { ConfigInstanceToken } from '../../interfaces/config.interface';
import { CustomInjector } from '../../services/custom-injector/custom-injector';
import { TimesheetAndExpensesUtilities } from '../../utilities/timesheet-and-expenses-utilities';
import { DateModelOptions } from '../options/date-model-options';
import { RadioModelOptions } from '../options/radio-model-options';
import { TimeModelOptions } from '../options/time-model-options';
import { DateModel } from '../primitive/date.model';
import { TextModel } from '../primitive/text.model';
import { TimeModel } from '../primitive/time.model';
import { CompositeModel } from './composite.model';
import { RadioModel } from './radio.model';
import { getNextTimesheetEntryCounter } from './timesheet-entry-counter';
import { TimesheetEntryInterface } from './timesheet-entry.interface';

export class TimesheetEowModel extends CompositeModel implements TimesheetEntryInterface {
    public startDate: DateModel;
    public startTime: TimeModel;
    public endDate: DateModel;
    public endTime: TimeModel;
    public endAddressOptions: RadioModel<TextModel>;
    private endTimeFlexibleHours: number;
    private readonly endTimeFlexibleHoursUnit = 'hours';

    public readonly index: number;
    public constructor(
        time: moment.Moment,
        private lastTimesheetEntry: moment.Moment,
        endAddress: string,
    ) {
        super('TimesheetEoWModel');

        const injector = CustomInjector.getInjector();
        const configService = injector.get(ConfigInstanceToken);
        const timesheetAndExpensesUtilities = injector.get(TimesheetAndExpensesUtilities);

        this.index = getNextTimesheetEntryCounter();

        // allow end of work to be a set amount in the future
        this.endTimeFlexibleHours = configService.getEowFlexibleHours();

        const modelOptions = {
            readonly: false,
            minValue: this.lastTimesheetEntry,
            maxValue: this.getEoWMaxValueOption(),
        };

        this.startDate = new DateModel('StartDate', time, new DateModelOptions(modelOptions));
        this.startTime = new TimeModel('StartTime', time, new TimeModelOptions(modelOptions));
        this.endDate = new DateModel('EndDate', time, new DateModelOptions(modelOptions));
        this.endTime = new TimeModel('EndTime', time, new TimeModelOptions(modelOptions));

        if (configService.getDisplayTimesheetAddressOptions()) {
            this.endAddressOptions = <RadioModel<TextModel>>this.set(
                new RadioModel<TextModel>(
                    'AddrNumber',
                    timesheetAndExpensesUtilities.getTimesheetAddressOptions(),
                    new RadioModelOptions({
                        initialSelection: 2,
                    }),
                ),
            );
            this.endAddressOptions.selectByPredicate((entry) => endAddress === entry.model.value);
        }
    }

    /**
     * Method to update the End-of-Work options on the component.
     * When the user opens the timesheet component and waits 5 minutes, the boundary values for the maxValue needs to be
     * recalculated on the new current time.
     */
    public updateEoWMaxValueOption(): void {
        const maxValue = this.getEoWMaxValueOption();
        this.startDate.options = { ...this.startDate.options, maxValue };
        this.startTime.options = { ...this.startTime.options, maxValue };
        this.endDate.options = { ...this.endDate.options, maxValue };
        this.endTime.options = { ...this.endTime.options, maxValue };
    }

    /**
     * Method calculate the maximum value for the end of work.
     * It is possible that the last timesheet entry is a completed notification with end datetime in the future, past
     * the endTimeFlexibleHours. If that is the case, that future value must be the end of work time cutoff.
     * Clone is used, because I don't want previousEntry to change, if maxEoWTime is being mutated for some reason.
     *
     * @private
     * @returns moment.Moment
     */
    private getEoWMaxValueOption(): moment.Moment {
        const latestEndTimeFlexibleHours = moment().add(this.endTimeFlexibleHours, this.endTimeFlexibleHoursUnit);
        if (this.lastTimesheetEntry.isValid()) {
            return moment.max(latestEndTimeFlexibleHours, this.lastTimesheetEntry).clone();
        }
        return latestEndTimeFlexibleHours;
    }
}
